/**
 * Email/SMTP Instellingen Pagina
 */

import { useState, useEffect } from 'react';
import { bedrijfsgegevensService } from '../../services/bedrijfsgegevens.service';
import Toast from '../../components/Toast';
import './EmailInstellingen.css';

const EmailInstellingen = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Buro42',
    smtp_use_tls: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await bedrijfsgegevensService.get();

      setFormData({
        smtp_host: data.smtp_host || 'smtp.gmail.com',
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || '',
        smtp_password: data.smtp_password || '',
        smtp_from_email: data.smtp_from_email || '',
        smtp_from_name: data.smtp_from_name || 'Buro42',
        smtp_use_tls: data.smtp_use_tls !== false
      });

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon instellingen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await bedrijfsgegevensService.update(formData);

      setSuccess('Email instellingen succesvol opgeslagen');
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="email-instellingen-container">
        <div className="loading">Email instellingen laden...</div>
      </div>
    );
  }

  return (
    <div className="email-instellingen-container">
      <div className="page-header">
        <h1>Email / SMTP Instellingen</h1>
        <p className="subtitle">Configureer de email instellingen voor het verzenden van offertes</p>
      </div>

      <Toast message={error} type="error" onClose={() => setError(null)} autoClose={0} />
      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <div className="info-box">
        <h3>Gmail Configuratie</h3>
        <p>Voor Gmail moet je een "App Password" aanmaken:</p>
        <ol>
          <li>Ga naar je Google Account</li>
          <li>Security → 2-Step Verification (moet aan staan)</li>
          <li>App passwords → Selecteer app: Mail → Selecteer device: Other (Custom name)</li>
          <li>Kopieer het gegenereerde wachtwoord naar het SMTP Wachtwoord veld</li>
        </ol>
      </div>

      <form onSubmit={handleSubmit} className="email-form">
        <div className="form-section">
          <h2>SMTP Server</h2>

          <div className="form-group">
            <label htmlFor="smtp_host">SMTP Host *</label>
            <input
              type="text"
              id="smtp_host"
              value={formData.smtp_host}
              onChange={(e) => handleChange('smtp_host', e.target.value)}
              placeholder="smtp.gmail.com"
              required
            />
            <small>Voor Gmail: smtp.gmail.com</small>
          </div>

          <div className="form-group">
            <label htmlFor="smtp_port">SMTP Port *</label>
            <input
              type="number"
              id="smtp_port"
              value={formData.smtp_port}
              onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
              required
            />
            <small>Voor Gmail met TLS: 587 | Voor SSL: 465</small>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.smtp_use_tls}
                onChange={(e) => handleChange('smtp_use_tls', e.target.checked)}
              />
              <span>Gebruik TLS/STARTTLS</span>
            </label>
            <small>Voor Gmail: aanvinken (poort 587)</small>
          </div>
        </div>

        <div className="form-section">
          <h2>Authenticatie</h2>

          <div className="form-group">
            <label htmlFor="smtp_user">SMTP Gebruikersnaam/Email *</label>
            <input
              type="email"
              id="smtp_user"
              value={formData.smtp_user}
              onChange={(e) => handleChange('smtp_user', e.target.value)}
              placeholder="jouw-email@gmail.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="smtp_password">SMTP Wachtwoord *</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="smtp_password"
                value={formData.smtp_password}
                onChange={(e) => handleChange('smtp_password', e.target.value)}
                placeholder="App Password voor Gmail"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <small>Voor Gmail: gebruik een App Password (zie instructies hierboven)</small>
          </div>
        </div>

        <div className="form-section">
          <h2>Afzender</h2>

          <div className="form-group">
            <label htmlFor="smtp_from_email">Van Email Adres *</label>
            <input
              type="email"
              id="smtp_from_email"
              value={formData.smtp_from_email}
              onChange={(e) => handleChange('smtp_from_email', e.target.value)}
              placeholder="jouw-email@gmail.com"
              required
            />
            <small>Het email adres dat als afzender wordt gebruikt</small>
          </div>

          <div className="form-group">
            <label htmlFor="smtp_from_name">Van Naam</label>
            <input
              type="text"
              id="smtp_from_name"
              value={formData.smtp_from_name}
              onChange={(e) => handleChange('smtp_from_name', e.target.value)}
              placeholder="Buro42"
            />
            <small>De naam die als afzender wordt getoond</small>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Opslaan...' : 'Instellingen Opslaan'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmailInstellingen;
